import {
	LoginRequestDto,
	LogoutRequestDto,
	LoginSSORequestDto,
} from "@api-types/user/LoginRequestDto";
import {
	LoginResponseDto,
	LoginResponseUser,
	LoginUserMatchingResponseDto,
	RedirectLoginResponseDto,
} from "@api-types/user/LoginResponseDto";
import { AccountActivationRequestDto } from "@api-types/user/AccountActivationRequestDto";
import { ForcePasswordChangeRequestDto } from "@api-types/user/ForcePasswordChangeRequestDto";
import { UserMatchingRequestDto } from "@api-types/user/UserMatchingRequestDto";
import {
	UserTypes,
	getMvcApplicationHeaders,
	getWebApiUrl,
	httpClient,
} from "@utils";
import qs from "qs";
import { MitAuthDto } from "@api-types/integrations/MitAuthDto";
import { BankIdAuthDto } from "@api-types/integrations";
import CaptchaService from "@services/captcha/CaptchaService";

const requestHeaders = {
	"Content-Type": "application/json",
};
export default class UserApiService {
	private static get baseUrl(): string {
		return `${getWebApiUrl()}/api/user`;
	}

	static getRequestHeaders = (
		user: UserTypes = UserTypes.PRIMARY
	): HeadersInit => {
		const appHeaders = getMvcApplicationHeaders(user);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	};

	static async loginUser(
		userData: LoginRequestDto
	): Promise<LoginResponseDto | RedirectLoginResponseDto> {
		const headers = this.getRequestHeaders();
		headers["Content-Type" as keyof HeadersInit] =
			"application/x-www-form-urlencoded";
		const url = `${this.baseUrl}/login`;

		const token = await CaptchaService.executeCaptcha("login");

		if (token) {
			userData.captchaResponse = token;
		}

		return httpClient.post(url, qs.stringify(userData), headers, {
			responseType: "json",
		});
	}

	static async loginUserWithMit(
		mitAuthDto: MitAuthDto
	): Promise<LoginResponseDto> {
		const headers = this.getRequestHeaders();
		const url = `${this.baseUrl}/login/mit`;
		return httpClient.post(
			url,
			JSON.stringify(mitAuthDto),
			{ ...headers, ...requestHeaders },
			{ responseType: "json" }
		);
	}

	static async loginUserWithBankId(
		bankIdAuthData: BankIdAuthDto
	): Promise<LoginResponseDto> {
		const headers = this.getRequestHeaders();
		const url = `${this.baseUrl}/login/bankid`;
		return httpClient.post(
			url,
			JSON.stringify(bankIdAuthData),
			{ ...headers, ...requestHeaders },
			{ responseType: "json" }
		);
	}

	static async confirmUserMatching(
		userData: UserMatchingRequestDto
	): Promise<LoginUserMatchingResponseDto> {
		const url = `${this.baseUrl}/confirm-user-matching`;
		const authHeaders = this.getRequestHeaders();
		return httpClient.post(
			url,
			JSON.stringify(userData),
			{ ...requestHeaders, ...authHeaders },
			{
				responseType: "json",
			}
		);
	}

	static async ssoLogin(data: LoginSSORequestDto): Promise<LoginResponseDto> {
		const url = `${this.baseUrl}/sso`;
		const headers = this.getRequestHeaders();
		const response = httpClient.get(
			`${url}?token=${data.token}`,
			{ ...headers, ...requestHeaders },
			{ responseType: "json" }
		);
		return response;
	}

	static async gamesSsoLogin(token: string): Promise<LoginResponseDto> {
		const url = `${this.baseUrl}/login/games-sso`;
		const headers = this.getRequestHeaders();
		const response = await httpClient.get(
			`${url}?token=${token}`,
			{ ...headers, ...requestHeaders },
			{ responseType: "json" }
		);
		return response;
	}

	static async logoutUser(requestModel: LogoutRequestDto): Promise<void> {
		const url = `${this.baseUrl}/logout`;
		const headers = this.getRequestHeaders();
		await httpClient.post(url, JSON.stringify(requestModel), {
			...headers,
			...requestHeaders,
		});
	}

	static async accountActivation(
		requestModel: AccountActivationRequestDto
	): Promise<LoginResponseDto> {
		const url = `${this.baseUrl}/account-activation`;
		const headers = this.getRequestHeaders();
		return httpClient.post(
			url,
			JSON.stringify(requestModel),
			{ ...headers, ...requestHeaders },
			{ responseType: "json" }
		);
	}

	static async forcePasswordChange(
		requestModel: ForcePasswordChangeRequestDto
	): Promise<LoginResponseDto> {
		const url = `${this.baseUrl}/force-password-change`;
		const headers = this.getRequestHeaders();
		return httpClient.post(
			url,
			JSON.stringify(requestModel),
			{ ...headers, ...requestHeaders },
			{ responseType: "json" }
		);
	}

	static async acceptGDPRConsent(): Promise<boolean> {
		const url = `${this.baseUrl}/accept-gdpr`;
		const headers = this.getRequestHeaders();
		return httpClient.get(
			url,
			{ ...headers, ...requestHeaders },
			{ responseType: "json" }
		);
	}

	static async refreshUser(
		userId: string,
		isPrimary: boolean
	): Promise<LoginResponseUser> {
		const url = `${this.baseUrl}/refresh-user?userId=${userId}`;
		const headers = this.getRequestHeaders(
			isPrimary ? UserTypes.PRIMARY : UserTypes.SECONDARY
		);
		return httpClient.get(
			url,
			{ ...headers, ...requestHeaders },
			{ responseType: "json" }
		);
	}
}
